import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Heart,
  Star,
  MapPin,
  Clock,
  ArrowRight,
  Sparkles,
  Check,
  AlertCircle,
} from "lucide-react";
import "./TrendingHoneymoonPackages.css";

const API_URL = "https://admin.jayrajgroup.com/api/honeymoon/packages";
const SKELETON_COUNT = 3;

const formatINR = (value) => {
  const num = Number(value);
  if (!num || num <= 0 || Number.isNaN(num)) return null;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `₹${num}`;
  }
};

const TrendingHoneymoonPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishlist, setWishlist] = useState({});

  useEffect(() => {
    const abortController = new AbortController();

    const fetchPackages = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(API_URL, { signal: abortController.signal });
        const result = await response.json();
        if (result.status && Array.isArray(result.data)) {
          setPackages(result.data);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
          console.error("Error fetching honeymoon packages:", err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
    return () => abortController.abort();
  }, []);

  const toggleWishlist = (id) => {
    setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section className="thp-section">
      <span className="thp-orb thp-orb-a" aria-hidden="true" />
      <span className="thp-orb thp-orb-b" aria-hidden="true" />

      <div className="thp-shell">
        {/* ---------- Header ---------- */}
        <motion.header
          className="thp-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="thp-title">Trending Honeymoon Packages</h2>
          <p className="thp-subtitle">
            Unforgettable romantic journeys crafted for extraordinary beginnings
            and everlasting memories.
          </p>
        </motion.header>

        {/* ---------- Body ---------- */}
        {error ? (
          <div className="thp-empty thp-empty-error">
            <span className="thp-empty-icon">
              <AlertCircle />
            </span>
            <h3 className="thp-empty-title">Couldn’t load packages</h3>
            <p className="thp-empty-text">Please try again in a moment.</p>
          </div>
        ) : loading ? (
          <div className="thp-grid" aria-busy="true" aria-label="Loading packages">
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <div key={i} className="thp-skeleton" />
            ))}
          </div>
        ) : packages.length === 0 ? (
          <div className="thp-empty">
            <span className="thp-empty-icon">
              <AlertCircle />
            </span>
            <h3 className="thp-empty-title">No packages yet</h3>
            <p className="thp-empty-text">
              New romantic escapes are being added soon.
            </p>
          </div>
        ) : (
          <div className="thp-grid">
            {packages.map((pkg, index) => {
              const originalPrice = pkg.plans_starting_from * 1.25;
              const isLiked = !!wishlist[pkg.id];
              const currentPrice = formatINR(pkg.plans_starting_from);
              const oldPrice = formatINR(originalPrice);

              return (
                <motion.article
                  key={pkg.id}
                  className="thp-card"
                  initial={{ opacity: 0, y: 26 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, delay: Math.min(index, 6) * 0.08 }}
                >
                  {/* ---------- Media ---------- */}
                  <div className="thp-media">
                    <img
                      src={pkg.featured_photo_url}
                      alt={pkg.name}
                      className="thp-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/800x600?text=No+Image";
                      }}
                    />

                    <div className="thp-badges">
                      <span className="thp-chip thp-chip-trending">
                        <Sparkles size={11} aria-hidden="true" /> Trending
                      </span>
                      <span className="thp-chip thp-chip-discount">20% OFF</span>
                    </div>

                  </div>

                  {/* ---------- Body ---------- */}
                  <div className="thp-body">
                    <div className="thp-meta">
                      <span className="thp-meta-loc">
                        <MapPin size={13} aria-hidden="true" />
                        {pkg.destination?.name || "Global"}
                      </span>
                      <span className="thp-meta-rating">
                        <Star
                          size={13}
                          fill="currentColor"
                          aria-hidden="true"
                        />
                        4.9
                      </span>
                    </div>

                    <h3 className="thp-card-title">{pkg.name}</h3>

                    <div className="thp-duration">
                      <Clock size={13} aria-hidden="true" />
                      {pkg.duration_nights} Nights / {pkg.duration_days} Days
                    </div>

                    <div className="thp-footer">
                      <div className="thp-price">
                        <div className="thp-price-stack">
                          {oldPrice && (
                            <span className="thp-old-price">{oldPrice}</span>
                          )}
                          <span className="thp-current-price">
                            {currentPrice || "—"}
                          </span>
                        </div>
                        <span className="thp-price-unit">per couple</span>
                      </div>

                      <a
                        href={`/honeymoon/packages/${pkg.slug}`}
                        className="thp-cta"
                      >
                        View Details
                        <ArrowRight size={14} aria-hidden="true" />
                      </a>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}

        {/* ---------- View All ---------- */}
        <motion.div
          className="thp-view-all"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <a href="/honeymoon/packages" className="thp-view-all-link">
            <span>View All Packages</span>
            <ArrowRight className="thp-view-all-icon" aria-hidden="true" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default TrendingHoneymoonPackages;